import { useState, useEffect, useRef, FormEvent, KeyboardEvent } from "react";
import { createFilterOptions, updateActiveTask, Util } from '../../util/Util'
import { Item } from './SupplyContext'

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../components/RefreshButton";
import QuantityPackHelper from "../../components/QuantityPackHelper";
import { SelectInstance } from "react-select";
import { useSupply } from "./SupplyContext";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useAuth } from "../../hooks/useAuth";
import { MYSQL_LIMITS } from "../../util/constants";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, parseMoneyInput, qtyMin } from "../../util/quantity";
import { Button, Checkbox, Grid, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AsynchronousPrice from "../../components/PriceAutoComplete";
import { focusAndSelect, isPlainEnter } from "../../util/focusField";
import { renderCatalogItemOption } from "../../components/itemAutocompleteRenderOption";
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from "../../components/catalogItemAutocomplete";
import { applySavedCatalogOption, CatalogItemPickerProvider } from "../../components/CatalogItemActions";
import type { CatalogItemRow } from "../inventory/ItemDetailModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "../../components/MoleDatePicker";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import VirtualizedListbox from "../../components/VirtualizedListbox";

interface Props {
	bundle?: any;
}

interface OptionType {
	value: number;
	label: string;
}

const SupplyItemDetail = ({ bundle }: Props) => {
	const [barcodes, setBarcodes] = useState<any>({});
	const [selectedItem, setSelectedItem] = useState<Item | null>(null);
	const [errorInput, setErrorInput] = useState("");
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [isPriceLoading, setIsPriceLoading] = useState(false);
	const [prices, setPrices] = useState<any[]>([]);
	const [useBarcode, setUseBarcode] = useState(false);
	const [barcode, setBarcode] = useState("");

	const { setPendingTasks, activeTask, items, setItems, apiRef } = useSupply();
	const { queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const { api } = useAuth();

	const barcodeRef = useRef<HTMLInputElement>(null);
	const itemRef = useRef<SelectInstance<OptionType, false>>(null);
	const quantityRef = useRef<HTMLInputElement>(null);
	const priceRef = useRef<HTMLInputElement>(null);
	const errorMessageRef = useRef<string>("");

	const fetchItems = async () => {
		setIsItemLoading(true);

		const res = await api.get(`items`);

		if (res) {
			if (res.status === 200) {
				setItems(res.data.items);
				setBarcodes(res.data.barcodes);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchItems);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsItemLoading(false);
	};

	const fetchPrices = async () => {
		setPrices([]);  // Clear previous prices
		if ((selectedItem?.id || -1) < 1) return;

		setIsPriceLoading(true);

		const res = await api.get(`items/${selectedItem?.id}/price_history`);

		if (res) {
			if (res.status === 200) {
				setPrices(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchPrices);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsPriceLoading(false);
	};

	const handleCatalogSaved = (updated: CatalogItemRow) => {
		setItems(applySavedCatalogOption(items, updated));
		setSelectedItem((prev) =>
			prev && prev.id === updated.id
				? new Item({
					...prev,
					item: updated.ItemName || prev.item,
					item_code: updated.ItemCode || prev.item_code,
					isFractional: updated.IsFractional === true,
				})
				: prev
		);
		updateActiveTask(setPendingTasks, (prev) => ({
			...prev,
			items: prev.items?.map((it) =>
				it.id === updated.id
					? new Item({
						...it,
						item: updated.ItemName || it.item,
						item_code: updated.ItemCode || it.item_code,
						isFractional: updated.IsFractional === true,
					})
					: it
			),
		}));
	};

	const onItemChanged = (item_id: number) => {
		const item: any = items.find((item: any) => item.id === item_id);

		if (item) {
			const myItem = new Item({
				id: item.id,
				item_code: item.ItemCode,
				item: item.ItemName,
				price: item.CostPrice,
				stock: item.Stock,
				quantity: 1,
				expiry: selectedItem?.expiry ?? null,
				category: item.Category,
				isFractional: item.IsFractional === true,
			});

			setSelectedItem(myItem);
			focusAndSelect(quantityRef);
		} else {
			setSelectedItem(null);
		}
	};

	const onExpiryChanged = (expiry: Date | null = new Date()) => {
		setSelectedItem((prev: Item | null) => {
			if (!prev) return null;
			return new Item({ ...prev, expiry });
		});
	};

	const lookupBarcode = (code: string = barcode) => {
		const itemID = barcodes[code];

		if (itemID) {
			onItemChanged(itemID);
		} else {
			itemRef.current?.focus();
			setErrorInput("barcodeInput");
			setTimeout(() => setErrorInput(""), 2000);
		}
	}

	const validateQty = (value: number): boolean => {
		let error = false;

		if (value < qtyMin(!!selectedItem?.isFractional) || value > QTY_LIMITS.max) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityRange", { max: Util.formatNumber(QTY_LIMITS.max) });
		} else if (selectedItem && !selectedItem.isFractional && !isWholeNumber(value)) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityMustBeWhole");
		}

		if (error) {
			setErrorInput("quantityInput");
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);
		}

		return !error;
	}

	const validateAmt = (value: number): boolean => {
		if (isNaN(value) || value < 0 || value > MONEY_LIMITS.max) {
			setErrorInput("amountInput");
			setTimeout(() => setErrorInput(""), 2000);
			return false;
		}
		return true;
	}

	const onAddClicked = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (selectedItem && activeTask) {
			if (!validateQty(selectedItem.quantity) || !validateAmt(selectedItem.price)) return;

			const existingIndex = activeTask.items!.findIndex(r => Util.compareStrings(r.uniqueId, selectedItem.uniqueId));

			let mergedRow = new Item({ ...selectedItem });

			if (existingIndex > -1) {
				// add quantities together
				mergedRow.quantity += activeTask.items![existingIndex].quantity;

				if (apiRef.current) {
					const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId) + 1;

					if (mergedRow.quantity > QTY_LIMITS.max) {
						errorMessageRef.current = t("common:validation.mergeExceedsLimit", { row: rowIndex, max: Util.formatNumber(QTY_LIMITS.max) });
						setErrorInput("quantityInput");
						setTimeout(() => {
							setErrorInput("");
							errorMessageRef.current = ""
						}, 5000);
						return;
					} else {
						toast.info(t("common:info.mergedWithRow", { row: rowIndex, quantity: mergedRow.quantity }));
					}
				}
			}

			updateActiveTask(setPendingTasks, (prev) => {
				const updatedItems = existingIndex > -1
					? prev.items!.map((item, idx) => (idx === existingIndex ? mergedRow : item))
					: [...prev.items!, mergedRow];

				return { ...prev, items: updatedItems };
			});
		}

		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	};

	useEffect(() => {
		fetchItems();
	}, []);

	useEffect(() => {
		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	}, [useBarcode]);

	useEffect(() => {
		setUseBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, []);

	// Enter in quantity moves to the price; Enter there adds the line (form submit).
	const onQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (!isPlainEnter(event)) return;
		event.preventDefault();
		focusAndSelect(priceRef);
	};

	return (
		<form onSubmit={onAddClicked} style={{ marginBottom: '10px' }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				<Grid size={{ xs: 12, sm: 6, md: 2 }} >
					<TextField
						id="barcodeInput"
						size="small"
						label={
							<span >
								{t("common:ui.labels.barcode")}&nbsp;&nbsp;
								<Checkbox
									checked={useBarcode}
									onChange={() => setUseBarcode(!useBarcode)}
								/>
							</span>
						}
						variant="filled"
						fullWidth
						type="text"
						value={barcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={barcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupBarcode()} />,
							}
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								lookupBarcode(barcode);
							}
						}}
						onChange={(e) => setBarcode(e.target.value)}
						disabled={!activeTask || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === "barcodeInput"}
						helperText={
							errorInput === "barcodeInput"
								? t("common:stockTrace.barcodeNotFound")
								: ""
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }} >
					<CatalogItemPickerProvider
						depotIds={[activeTask?.depot]}
						onItemSaved={handleCatalogSaved}
					>
					<Autocomplete
						id="itemInput"
						size="small"
						options={items}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ""}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) => renderCatalogItemOption(props, option, "both")}
						value={items.find((i) => i.id === selectedItem?.id) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => onItemChanged(id),
								onClear: () => onItemChanged(-1),
							});
						}}
						loading={isItemLoading}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t("common:ui.labels.item")}&nbsp;&nbsp;
										<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
									</span>
								}
								placeholder={isItemLoading ? t("common:ui.placeholders.loadingItems") : t("common:ui.placeholders.selectItem")}
								required
								onFocus={(e) => e.target.select()}
								inputRef={itemRef}
								variant="filled"
								fullWidth
							/>
						)}
						sx={{
							'& .MuiAutocomplete-listbox': {
								padding: 0,             // remove extra padding around list
							},
							'& .MuiAutocomplete-option': {
								padding: '1px 1px',     // make each option compact
								fontSize: '5rem',   // smaller font
								minHeight: 'auto',      // remove default minHeight
							},
							'& .MuiAutocomplete-popupIndicator': {
								padding: '4px',         // smaller icon padding
							},
						}}
						ListboxProps={{
							style: {
								maxHeight: 400,
							},
						}}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				{/* <Grid size={{ xs: 12, sm: 6, md: 2 }} >
					<TextField
						id="newBarcodeInput"
						size="small"
						label={t("common:ui.labels.setBarcode")}
						variant="filled"
						fullWidth
						type="text"
						value={selectedItem?.new_barcode ?? ""}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						onFocus={(e) => e.target.select()}
						onChange={(e) => {
							setSelectedItem((prev: Item | null) => {
								if (!prev) return null;
								return new Item({ ...prev, new_barcode: e.target.value });
							});
						}}
						disabled={!selectedItem}
					/>
				</Grid> */}
				<Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<QuantityPackHelper
						id="quantityInput"
						size="small"
						label={t("common:ui.labels.quantity")}
						fullWidth
						required
						disabled={!selectedItem}
						value={selectedItem?.quantity ?? 1}
						min={qtyMin(!!selectedItem?.isFractional)}
						max={QTY_LIMITS.max}
						fractional={selectedItem?.isFractional}
						inputRef={quantityRef}
						onKeyDown={onQuantityKeyDown}
						onChange={(newValue) => {
							if (validateQty(newValue)) {
								setSelectedItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({ ...prev, quantity: newValue });
								});
							}
						}}
						error={errorInput === "quantityInput"}
						helperText={errorInput === "quantityInput" ? errorMessageRef.current : undefined}
					/>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<AsynchronousPrice
						fetchPrices={fetchPrices}
						prices={prices}
						selectedItem={selectedItem}
						setSelectedItem={setSelectedItem}
						itemConstructor={Item}
						isPriceLoading={isPriceLoading}
						inputRef={priceRef}
					/>
				</Grid>
				{/* <Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<TextField
						id="sellingPriceInput"
						size="small"
						label={t("common:tables.sellingPrice")}
						variant="filled"
						fullWidth
						type="number"
						value={selectedItem?.selling_price ?? 0}
						inputProps={{ min: 0 }}
						onFocus={(e) => e.target.select()}
						onChange={(e) => {
							const newValue = parseMoneyInput(e.target.value);
							if (newValue != null && newValue >= 0 && newValue <= MONEY_LIMITS.max) {
								setSelectedItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({ ...prev, selling_price: newValue });
								});
							}
						}}
						disabled={!selectedItem}
					/>
				</Grid> */}
				<Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							label={
								<span>
									{t("common:tables.expiry")}:&nbsp;&nbsp;
									<RefreshButton isWorking={false} onClick={onExpiryChanged} />
								</span>
							}
							value={selectedItem?.expiry ?? null}
							onChange={(newDate) => {
								if (!newDate) {
									onExpiryChanged(null);
									return;
								}
								if (Util.compareStrings(newDate.toString(), "Invalid Date")) {
									setErrorInput("dateInput");
									setTimeout(() => setErrorInput(""), 1500);
									return;
								}
								const date = newDate instanceof Date ? newDate : newDate.toDate();
								if (Number.isNaN(date.getTime())) {
									setErrorInput("dateInput");
									setTimeout(() => setErrorInput(""), 1500);
									return;
								}
								onExpiryChanged(date);
							}}
							disabled={!activeTask}
							slotProps={{
								textField: {
									id: "dateInput",
									size: "small",
									fullWidth: true,
									variant: "filled",
									error: errorInput === "dateInput",
									helperText:
										errorInput === "dateInput" ? t("common:validation.invalidDate") : "",
								},
								field: {
									clearable: true,
								},
							}}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
					<Button fullWidth size="large" type="submit" color="success" startIcon={<Add />} sx={{ fontWeight: 'bolder' }}>
						{t("common:ui.buttons.add")}
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export default SupplyItemDetail;
